// src/pages/crewMember/CrewDetail/forms/CreateCrewMember.form.controller.jsx

import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";

// components
import SideViewModal from "../../../../components/modals/SideViewModal/SideViewModal.component";
import AddCrewMember from "../AddCrewMember.form";

// apollo
import { useMutation, useQuery } from "@apollo/client";
import { CREATE_CREW_MEMBER } from "../../../../graphql/crewMember/crewMember.mutations";
import {
  GET_ALL_CREW_MEMBERS_WITH_FULL_DETAIL,
  GET_ALL_FOREMAN_USERS,
} from "../../../../graphql/crewMember/crewMember.queries";

// utils
import { validate } from "../../../../shared/utility";

// feedback (MUI)
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// ---------------- validation schema ----------------
const validationSchema = {
  firstName: { type: "input", validation: { required: true } },
  lastName: { type: "input", validation: { required: true } },
  street1: { type: "input", validation: {} },
  street2: { type: "input", validation: {} },
  city: { type: "input", validation: {} },
  state: { type: "input", validation: {} },
  zipcode: { type: "input", validation: {} },
  classification: { type: "input", validation: { required: true } },
  certifications: {
    type: "list",
    fields: {
      name: { type: "input", validation: { required: true } },
      expirationDate: {
        type: "date",
        validation: { required: true, isDate: true },
      },
    },
  },
  emergencyContacts: {
    type: "list",
    fields: {
      firstName: { type: "input", validation: { required: true } },
      lastName: { type: "input", validation: { required: true } },
      phone: { type: "input", validation: { required: true } },
    },
  },
};

const MAX = {
  firstName: 100,
  lastName: 100,
  street1: 255,
  street2: 255,
  city: 100,
  state: 100,
  zipcode: 20,
  phone: 36,
};

// ---------------- classification helpers ----------------
const ENUM_TOKENS = [
  "SUPERINTENDENT",
  "FOREMAN",
  "YARD",
  "CLERK",
  "JOURNEYMAN",
  "A1",
  "A2",
  "A3",
  "A4",
  "A5",
  "A6",
  "A7",
  "A8",
  "ADMIN",
];
const ALLOWED_CLASSIFICATIONS = new Set(ENUM_TOKENS);

const CLASSIFICATION_ALIAS = {
  S: "SUPERINTENDENT",
  F: "FOREMAN",
  Y: "YARD",
  C: "CLERK",
  J: "JOURNEYMAN",
  1: "A1",
  2: "A2",
  3: "A3",
  4: "A4",
  5: "A5",
  6: "A6",
  7: "A7",
  8: "A8",
  A: "ADMIN",
  SUPERINTENDENT: "SUPERINTENDENT",
  FOREMAN: "FOREMAN",
  YARD: "YARD",
  CLERK: "CLERK",
  JOURNEYMAN: "JOURNEYMAN",
  A1: "A1",
  A2: "A2",
  A3: "A3",
  A4: "A4",
  A5: "A5",
  A6: "A6",
  A7: "A7",
  A8: "A8",
  ADMIN: "ADMIN",
};

function normalizeClassification(input) {
  let raw = input;
  if (raw && typeof raw === "object" && "value" in raw) raw = raw.value;
  raw = String(raw ?? "").trim();
  if (!raw) return raw;

  const upper = raw.toUpperCase().replace(/\s+/g, "_");
  const mapped =
    CLASSIFICATION_ALIAS[raw] ??
    CLASSIFICATION_ALIAS[upper] ??
    CLASSIFICATION_ALIAS[raw.toUpperCase()] ??
    upper;

  if (!ALLOWED_CLASSIFICATIONS.has(mapped)) {
    throw new Error(
      `Invalid classification: "${raw}". Must be one of ${ENUM_TOKENS.join(
        ", "
      )}`
    );
  }
  return mapped;
}

function clampStrings(obj, fieldMaxMap = MAX) {
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    let v = out[k];
    // If it's a small {value, label} object from a Select
    if (
      v &&
      typeof v === "object" &&
      "value" in v &&
      Object.keys(v).length <= 3
    ) {
      v = v.value;
    }
    if (typeof v === "string") {
      v = v.trim();
      if (fieldMaxMap[k]) v = v.slice(0, fieldMaxMap[k]);
    }
    out[k] = v;
  }
  return out;
}

// Optional submit normalization (used if you wire handleSubmit → onSubmit)
function normalizeSubmit(formData) {
  let out = clampStrings(formData);

  if (out.classification) {
    out.classification = normalizeClassification(out.classification);
  }

  // userId: '' | string -> Int or null for backend
  if (out.userId === "" || out.userId === null || out.userId === undefined) {
    out.userId = null;
  } else {
    const parsed = parseInt(out.userId, 10);
    out.userId = Number.isNaN(parsed) ? null : parsed;
  }

  if (Array.isArray(out.certifications)) {
    out.certifications = out.certifications.map((c) => {
      const cc = clampStrings(c, { name: 100 });
      if (cc.expirationDate?.format) {
        cc.expirationDate = cc.expirationDate.format("YYYY-MM-DD");
      }
      return cc;
    });
  }

  if (Array.isArray(out.emergencyContacts)) {
    out.emergencyContacts = out.emergencyContacts.map((e) => {
      const ee = clampStrings(e, {
        firstName: 100,
        lastName: 100,
        phone: 36,
      });
      return ee;
    });
  }

  return out;
}

// ---------------- error helpers ----------------

// Map low-level DB / constraint errors to user-friendly messages
function humanizeErrorMessage(msg) {
  if (!msg) return msg;
  const lower = msg.toLowerCase();

  // Unique constraint on crew_crewmember.user_id (one user per crew member)
  if (
    lower.includes("crew_crewmember_user_id_key") ||
    (lower.includes("duplicate key value") && lower.includes("user_id")) ||
    (lower.includes("user_id") && lower.includes("already exists"))
  ) {
    return "That user account is already assigned to another crew member. Please choose a different user.";
  }

  // You can add more mappings here if you want to hide other technical errors

  return msg;
}

// Extract meaningful messages from Apollo error, then humanize them
function extractApolloError(err) {
  const msgs = [];

  if (err?.graphQLErrors?.length) {
    for (const ge of err.graphQLErrors) {
      if (ge?.message) msgs.push(ge.message);
      const det = ge?.extensions?.exception?.detail || ge?.extensions?.detail;
      if (det) msgs.push(det);
      const fieldErrors = ge?.extensions?.exception?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        for (const [f, arr] of Object.entries(fieldErrors)) {
          msgs.push(
            `${f}: ${Array.isArray(arr) ? arr.join(", ") : String(arr)}`
          );
        }
      }
    }
  }

  if (err?.networkError?.result?.errors?.length) {
    for (const e of err.networkError.result.errors) {
      if (e?.message) msgs.push(e.message);
    }
  }

  if (!msgs.length && err?.networkError?.message) {
    msgs.push(err.networkError.message);
  }

  if (!msgs.length && err?.message) {
    msgs.push(err.message);
  }

  const raw = msgs.join("\n");
  return humanizeErrorMessage(raw);
}

// ---------------- component ----------------

export default function CreateCrewMemberFormController({ visible, closeForm }) {
  const history = useHistory();

  const [errors, setErrors] = useState({
    emergencyContacts: [],
    certifications: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  // Load all foreman users for dropdown
  const {
    data: foremanUsersData,
    loading: foremanUsersLoading,
    error: foremanUsersError,
  } = useQuery(GET_ALL_FOREMAN_USERS, {
    skip: !visible,
  });

  // Mutation to create crew member
  const [createCrewMember, { loading }] = useMutation(CREATE_CREW_MEMBER, {
    refetchQueries: [{ query: GET_ALL_CREW_MEMBERS_WITH_FULL_DETAIL }],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      console.log("Crew member created successfully:", data);
      setIsSubmitting(false);
      setErrors({ emergencyContacts: [], certifications: [] });

      closeForm();

      setTimeout(() => {
        history.push("/crew?sortField=&sortDirection=&filterBy=active");
      }, 150);
    },
    onError: (e) => {
      let msg = extractApolloError(e) || "Something went wrong.";
      // extra safety, in case extractApolloError changes later
      msg = humanizeErrorMessage(msg);

      console.error(e);
      setIsSubmitting(false);
      setErrors((prev) => ({ ...prev, __server: msg }));
      setToastMsg(msg);
      setToastOpen(true);
    },
  });

  // Map foreman users to dropdown options
  const foremanUsersOptions = React.useMemo(() => {
    if (!foremanUsersData?.allForemanUsers) return [];

    return foremanUsersData.allForemanUsers.map((user) => ({
      value: String(user.id), // keep as string in the form
      label: `${user.firstName || ""} ${user.lastName || ""} (${
        user.username
      }) - ${user.email}`.trim(),
      userData: user,
    }));
  }, [foremanUsersData]);

  // Optional: local submit handler if you wire it into SideViewModal.onSubmit
  const handleSubmit = useCallback(
    (formData) => {
      if (isSubmitting || loading) return;

      const { isValid, errors: vErrors } = validate(formData, validationSchema);
      if (!isValid) {
        setErrors(vErrors);
        return;
      }

      try {
        const variables = normalizeSubmit(formData);
        setIsSubmitting(true);
        createCrewMember({ variables });
      } catch (e) {
        const msg = e.message || "Invalid classification.";
        setErrors((prev) => ({ ...prev, classification: msg }));
        setToastMsg(msg);
        setToastOpen(true);
      }
    },
    [isSubmitting, loading, createCrewMember]
  );

  useEffect(() => {
    if (foremanUsersError) {
      const msg =
        extractApolloError(foremanUsersError) ||
        "Failed to load foreman users.";
      setToastMsg(msg);
      setToastOpen(true);
    }
  }, [foremanUsersError]);

  useEffect(() => {
    if (foremanUsersData) {
      console.log("Foreman Users Data:", foremanUsersData);
      console.log("allForemanUsers:", foremanUsersData.allForemanUsers);
    }
  }, [foremanUsersData]);

  if (!visible) return null;

  const addVariants = [
    {
      key: "add-crew",
      name: "Add Crew Member",
      component: (
        <AddCrewMember
          foremanUsers={foremanUsersOptions}
          loadingForemanUsers={foremanUsersLoading}
        />
      ),
      stages: 2,
    },
  ];

  return (
    <>
      <SideViewModal
        show={visible}
        submitText="Add Crew Member"
        onClose={() => {
          setErrors({ emergencyContacts: [], certifications: [] });
          closeForm();
        }}
        title="New Crew Member"
        variants={addVariants}
        errors={errors}
        mutation={createCrewMember}
        successObjectKey="createCrewMember"
        entityName="Crew Member"
        operationType="create"
        refetchQueries={[GET_ALL_CREW_MEMBERS_WITH_FULL_DETAIL]}
        // If your SideViewModal supports custom submit, you can enable:
        // onSubmit={handleSubmit}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMsg}
        </Alert>
      </Snackbar>
    </>
  );
}
